import { SingleValue } from 'react-select';
import { DocResponse } from '../../model/response/doc.model';

export function getRowBoundaryKey(item: DocResponse): string | null {
  if (item.classCourseId != null) {
    return `classCourseId:${item.classCourseId}`;
  }
  if (item.classId != null) {
    return `classId:${item.classId}`;
  }
  if (item.projectId != null) {
    return `projectId:${item.projectId}`;
  }
  return null;
}

export function isValidDrop(
  sourceIndex: number,
  destIndex: number,
  items: DocResponse[]
): boolean {
  const sourceKey = getRowBoundaryKey(items[sourceIndex]);
  const destKey = getRowBoundaryKey(items[destIndex]);
  return sourceKey === destKey;
}

export function calculateOrderNumbers(
  items: DocResponse[]
): { id: string; orderNo: number }[] {
  const groupCounters: Record<string, number> = {};
  return items.map((item) => {
    const key = getRowBoundaryKey(item) ?? 'none';
    groupCounters[key] = (groupCounters[key] ?? 0) + 1;
    return {
      id: item.id,
      orderNo: groupCounters[key],
    };
  });
}

export function isRowDragDisabled(
  item: DocResponse,
  filtersActive: boolean,
  allowProjectLevelDrag: boolean = false
): boolean {
  return filtersActive || (item.projectId != null && !allowProjectLevelDrag);
}

export function matchesSearchTerm(caption: string, search: string): boolean {
  return search === '' || caption.toLowerCase().includes(search.toLowerCase());
}

export function matchesDocFilter(
  item: DocResponse,
  search: string,
  courseSelect: SingleValue<{ label: string; value: number }> | undefined
): boolean {
  const searchMatch = matchesSearchTerm(item.caption, search);
  const courseMatch = courseSelect === undefined || isNaN(courseSelect?.value as number)
    || item.projectId !== undefined || item.classId !== undefined || item.classCourseId === courseSelect?.value;
  return searchMatch && courseMatch;
}

export function findSpliceStartIndex(
  currentOrder: DocResponse[],
  paginatedItems: DocResponse[]
): number {
  if (paginatedItems.length === 0) return 0;
  return currentOrder.findIndex((item) => item.id === paginatedItems[0].id);
}

export function applyReorder(
  currentOrder: DocResponse[],
  paginatedItems: DocResponse[],
  newPaginated: DocResponse[]
): DocResponse[] {
  const newOrder = [...currentOrder];
  const positions = paginatedItems
    .map((item) => currentOrder.findIndex((i) => i.id === item.id))
    .sort((a, b) => a - b);
  positions.forEach((pos, index) => {
    newOrder[pos] = newPaginated[index];
  });
  return newOrder;
}

export function hasReorderChanged(
  current: DocResponse[],
  initial: DocResponse[]
): boolean {
  return current.some((item, index) => item.id !== initial[index]?.id);
}
